/**
 * Settings → Integrations → Shopify: the store connection (top of the card).
 *
 * The same three boxes as before (Shopify domain, Client ID, Client Secret).
 * On Save, PrintRun works out the app type by itself:
 *   - the app lives in the store's own Shopify account → it connects as before;
 *   - Blue Monk made the app in another Shopify account → Shopify answers
 *     `shop_not_permitted`. An org admin is asked to contact Blue Monk; a
 *     platform admin's save registers the custom app and the card switches to install
 *     mode (Dev Dashboard values to copy, install link, status, Start syncing);
 *   - the store has not installed the app yet → Shopify answers
 *     `app_not_installed`. A platform admin's save takes the same install-mode
 *     path; an org admin is told to install the app first (or ask Blue Monk).
 *
 * Roles: only a platform admin (`mode="platform"`; the platform admin list,
 * BLU-1313, never the 'Super Admin' role) sets up or changes a custom
 * app. An org admin sees a registered app read-only, install link included.
 * A store connected through the public PrintRun app is read-only for both:
 * it is managed from the app in the store's Shopify admin.
 */
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Loader2,
  Lock,
  Play,
  RefreshCw,
  Save,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ShopifySetupInstructions } from '@/components/onboarding/ShopifySetupInstructions';
import { formatDateTime, useDateFnsLocale } from '@/lib/format-dates';
import { AmberNote } from './shopify-connection/AmberNote';
import { CopyValue } from './shopify-connection/CopyValue';
import { SetupValues } from './shopify-connection/SetupValues';
import { LocalSecretInput, NoAutofillTextInput } from './shopify-connection/NoAutofillInputs';
import {
  customAppStatusStyle,
  failureReasonText,
  isShopifyInstallLink,
  modeStatusStyle,
  normalizeShopDomain,
  saveErrorText,
  scopePhrase,
  type ConnectionView,
  type CustomAppView,
} from './shopify-connection/presentation';
import {
  useExchangingStale,
  useShopifyConnection,
  type SaveResult,
  type SectionMode,
} from './shopify-connection/useShopifyConnection';

const SHOP_DOMAIN_PATTERN = /^[a-z0-9-]+\.myshopify\.com$/;

type Connection = ReturnType<typeof useShopifyConnection>;

export interface ShopifyConnectionSectionProps {
  organizationId: string;
  /** `platform` = platform admin (custom app setup); `org` = the org's own admins. */
  mode: SectionMode;
}

export function ShopifyConnectionSection({ organizationId, mode }: ShopifyConnectionSectionProps) {
  const { t } = useTranslation('settings');
  const connection = useShopifyConnection(organizationId, mode);

  if (connection.isLoading) {
    return (
      <div className="space-y-3" role="status" aria-busy="true">
        <div className="flex flex-wrap items-center gap-2">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-48" />
        </div>
        <Skeleton className="h-10 w-full max-w-prose" />
        <Skeleton className="h-10 w-full max-w-prose" />
        <span className="sr-only">{t('shopifyConnection.store.loading')}</span>
      </div>
    );
  }

  if (!connection.data) {
    return (
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-amber-950">
        <div className="flex min-w-0 gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('shopifyConnection.store.loadFailedTitle')}</p>
            <p className="text-xs">{t('shopifyConnection.store.loadFailedBody')}</p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 border-amber-300 bg-white/70 text-xs text-amber-900 hover:bg-amber-100 hover:text-amber-900"
          onClick={() => void connection.refetch()}
          disabled={connection.isFetching}
        >
          {connection.isFetching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {t('shopifyConnection.store.retry')}
        </Button>
      </div>
    );
  }

  const data = connection.data;
  if (data.customApp) {
    return <CustomAppConnection data={data} customApp={data.customApp} mode={mode} connection={connection} />;
  }
  return data.mode === 'public_app' ? <PublicAppConnection data={data} /> : <StandardConnection data={data} connection={connection} />;
}

// ─── Connected through the public PrintRun app (read-only) ───────────────────

function PublicAppConnection({ data }: { data: ConnectionView }) {
  const { t } = useTranslation('settings');
  return (
    <div className="space-y-2">
      <div className="space-y-1" role="status">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={modeStatusStyle(t, data.mode)} />
          {data.shopDomain && <span className="break-all text-sm font-medium">{data.shopDomain}</span>}
        </div>
        <p className="text-xs text-muted-foreground">{t('shopifyConnection.store.mode.publicApp')}</p>
      </div>
      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('shopifyConnection.store.publicApp.managed')}
      </p>
    </div>
  );
}

// ─── Not set up / connected via Client ID or access token ────────────────────

function StandardConnection({ data, connection }: { data: ConnectionView; connection: Connection }) {
  const { t } = useTranslation('settings');
  const [showInstructions, setShowInstructions] = useState(false);
  const modeText: Record<ConnectionView['mode'], string> = {
    none: t('shopifyConnection.store.mode.none'),
    client_credentials: t('shopifyConnection.store.mode.clientCredentials'),
    access_token: t('shopifyConnection.store.mode.accessToken'),
    public_app: t('shopifyConnection.store.mode.publicApp'),
    custom_app: t('shopifyConnection.store.mode.customApp'),
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1" role="status">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={modeStatusStyle(t, data.mode)} />
          {data.shopDomain && <span className="break-all text-sm font-medium">{data.shopDomain}</span>}
        </div>
        <p className="text-xs text-muted-foreground">{modeText[data.mode]}</p>
      </div>

      <Collapsible open={showInstructions} onOpenChange={setShowInstructions}>
        <CollapsibleTrigger asChild>
          <Button type="button" variant="outline" className="h-auto min-h-10 w-full justify-between gap-2 whitespace-normal text-left">
            <span className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t('shopifyConnection.store.instructions')}
            </span>
            {showInstructions ? (
              <ChevronUp className="h-4 w-4 shrink-0" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4">
          <div className="rounded-lg border bg-muted/50 p-4">
            <ShopifySetupInstructions />
          </div>
        </CollapsibleContent>
      </Collapsible>

      <CredentialsForm
        variant="connect"
        connection={connection}
        initialDomain={data.shopDomain ?? ''}
        initialClientId=""
        hasSavedCredentials={data.mode !== 'none'}
      />
    </div>
  );
}

// ─── Custom app (install mode) ───────────────────────────────────────────────

function CustomAppConnection({
  data,
  customApp,
  mode,
  connection,
}: {
  data: ConnectionView;
  customApp: CustomAppView;
  mode: SectionMode;
  connection: Connection;
}) {
  const { t } = useTranslation('settings');
  const locale = useDateFnsLocale();
  const isPlatform = mode === 'platform';
  const status = customApp.status;
  const [showChangeApp, setShowChangeApp] = useState(false);
  const [showSetup, setShowSetup] = useState(status !== 'connected');
  const [syncError, setSyncError] = useState<string | null>(null);
  // Still exchanging past the 60s lease: polling has stopped, say what to do.
  const stalled = useExchangingStale(customApp);

  const detail = stalled
    ? t('shopifyConnection.store.detail.exchangingStale')
    : statusDetail(t, customApp, isPlatform, formatDateTime(customApp.connectedAt, locale));
  const missingScopes = customApp.missingScopes.map((scope) => scopePhrase(t, scope));

  const onStartSync = async () => {
    setSyncError(null);
    try {
      const result = await connection.startSync();
      if (result.outcome === 'started') {
        toast.success(t('shopifyConnection.store.sync.startedTitle'), {
          description: t('shopifyConnection.store.sync.startedBody'),
        });
      } else if (result.outcome === 'not_connected') {
        setSyncError(t('shopifyConnection.store.sync.notConnected'));
      }
    } catch (error) {
      toast.error(t('shopifyConnection.store.sync.failed'), {
        description: error instanceof Error ? error.message : undefined,
        duration: Infinity,
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1" role="status" aria-live="polite">
        <div className="flex flex-wrap items-center gap-2">
          <span className="break-all text-sm font-medium">{customApp.shop}</span>
          <StatusBadge status={customAppStatusStyle(t, status)} />
          {status === 'exchanging' && !stalled && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-700 motion-reduce:animate-none" aria-hidden="true" />
          )}
        </div>
        <p className="text-sm text-foreground">{detail}</p>
        {status === 'connected' && customApp.statusReason && (
          // A failed (or interrupted) re-install of a store that stays connected on its existing token.
          <p className="text-xs text-muted-foreground">{t('shopifyConnection.store.detail.reconnectFailed')}</p>
        )}
        {(status === 'waiting' || status === 'failed') && isPlatform && (
          <p className="text-xs text-muted-foreground">{t('shopifyConnection.store.hint.checkSecret')}</p>
        )}
      </div>

      {status === 'connected' && missingScopes.length > 0 && (
        <AmberNote>
          {t(isPlatform ? 'shopifyConnection.store.scopesMissing.platform' : 'shopifyConnection.store.scopesMissing.org', {
            permissions: missingScopes.join(', '),
          })}
        </AmberNote>
      )}

      <SyncState
        customApp={customApp}
        isPlatform={isPlatform}
        onStart={() => void onStartSync()}
        starting={connection.isStartingSync}
        error={syncError}
      />

      {customApp.installLink ? (
        <CopyValue
          label={t('shopifyConnection.store.installLink.label')}
          value={customApp.installLink}
          openable
          hint={
            status !== 'connected' ? (
              <p className="text-xs text-muted-foreground">{t('shopifyConnection.store.installLink.help')}</p>
            ) : undefined
          }
        />
      ) : (
        !isPlatform && <p className="text-xs text-muted-foreground">{t('shopifyConnection.store.installLink.notYet')}</p>
      )}

      {isPlatform ? (
        <>
          <InstallLinkEditor connection={connection} current={customApp.installLink} />

          {data.setup && (
            <Collapsible open={showSetup} onOpenChange={setShowSetup}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm font-medium text-foreground transition-colors duration-200 hover:text-threadheads-purple motion-reduce:transition-none"
                >
                  {showSetup ? (
                    <ChevronUp className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  )}
                  {t('shopifyConnection.store.setup.title')}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                <SetupValues setup={data.setup} />
              </CollapsibleContent>
            </Collapsible>
          )}

          <Collapsible open={showChangeApp} onOpenChange={setShowChangeApp}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 text-sm font-medium text-foreground transition-colors duration-200 hover:text-threadheads-purple motion-reduce:transition-none"
              >
                {showChangeApp ? (
                  <ChevronUp className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                )}
                {t('shopifyConnection.store.changeApp.title')}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <CredentialsForm
                variant="changeApp"
                connection={connection}
                initialDomain={customApp.shop}
                initialClientId={customApp.clientId ?? ''}
                hasSavedCredentials
              />
            </CollapsibleContent>
          </Collapsible>
        </>
      ) : (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t('shopifyConnection.store.managedByBlueMonk')}
        </p>
      )}
    </div>
  );
}

function statusDetail(t: TFunction, customApp: CustomAppView, isPlatform: boolean, connectedAt: string): string {
  switch (customApp.status) {
    case 'connected':
      return customApp.connectedAt
        ? t('shopifyConnection.store.detail.connectedAt', { time: connectedAt })
        : t('shopifyConnection.store.detail.connected');
    case 'exchanging':
      return t('shopifyConnection.store.detail.exchanging');
    case 'failed':
      return failureReasonText(t, customApp.statusReason);
    case 'uninstalled':
      return t(isPlatform ? 'shopifyConnection.store.detail.uninstalledPlatform' : 'shopifyConnection.store.detail.uninstalledOrg');
    default:
      return t(isPlatform ? 'shopifyConnection.store.detail.waitingPlatform' : 'shopifyConnection.store.detail.waitingOrg');
  }
}

function SyncState({
  customApp,
  isPlatform,
  onStart,
  starting,
  error,
}: {
  customApp: CustomAppView;
  isPlatform: boolean;
  onStart: () => void;
  starting: boolean;
  error: string | null;
}) {
  const { t } = useTranslation('settings');
  if (customApp.status !== 'connected') {
    return isPlatform ? <p className="text-xs text-muted-foreground">{t('shopifyConnection.store.sync.afterConnect')}</p> : null;
  }
  if (customApp.syncEnabled) {
    return <p className="text-sm text-foreground">{t('shopifyConnection.store.sync.on')}</p>;
  }
  if (!isPlatform) {
    return <p className="text-sm text-foreground">{t('shopifyConnection.store.sync.pausedOrg')}</p>;
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium">{t('shopifyConnection.store.sync.pausedTitle')}</p>
        <p className="text-xs text-muted-foreground">{t('shopifyConnection.store.sync.pausedBody')}</p>
        {error && (
          <p className="mt-1 text-xs text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
      <Button type="button" size="sm" className="h-8 shrink-0 gap-1.5" onClick={onStart} disabled={starting}>
        {starting ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <Play className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {t('shopifyConnection.store.sync.start')}
      </Button>
    </div>
  );
}

function InstallLinkEditor({ connection, current }: { connection: Connection; current: string | null }) {
  const { t } = useTranslation('settings');
  const inputId = useId();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const trimmed = value.trim();

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isShopifyInstallLink(trimmed)) {
      setError(t('shopifyConnection.store.installLink.invalid'));
      return;
    }
    setError(null);
    try {
      const result = await connection.saveInstallLink(trimmed);
      if (result.outcome === 'not_registered') {
        setError(t('shopifyConnection.store.installLink.notRegistered'));
        return;
      }
      setValue('');
      toast.success(t('shopifyConnection.store.installLink.saved'));
    } catch (caught) {
      toast.error(t('shopifyConnection.store.installLink.saveFailed'), {
        description: caught instanceof Error ? caught.message : undefined,
        duration: Infinity,
      });
    }
  };

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="max-w-prose space-y-2" noValidate>
      <Label htmlFor={inputId}>
        {current ? t('shopifyConnection.store.installLink.replaceLabel') : t('shopifyConnection.store.installLink.addLabel')}
      </Label>
      <div className="flex flex-wrap items-start gap-2">
        <NoAutofillTextInput
          id={inputId}
          name="shopify-install-link"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            if (error) setError(null);
          }}
          placeholder="https://admin.shopify.com/..."
          className="min-w-0 flex-1 basis-60"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
          disabled={connection.isSavingInstallLink}
        />
        <Button type="submit" size="sm" className="h-10 shrink-0" disabled={trimmed === '' || connection.isSavingInstallLink}>
          {connection.isSavingInstallLink ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
          )}
          {t('shopifyConnection.store.installLink.save')}
        </Button>
      </div>
      {error && (
        <p id={`${inputId}-error`} className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

// ─── The three boxes ─────────────────────────────────────────────────────────

interface CredentialsFormProps {
  /** `connect`: set up or update a store. `changeApp`: a platform admin edits a registered custom app. */
  variant: 'connect' | 'changeApp';
  connection: Connection;
  initialDomain: string;
  initialClientId: string;
  /** Something is already saved, so blank secret boxes mean "keep the current one". */
  hasSavedCredentials: boolean;
}

function CredentialsForm({ variant, connection, initialDomain, initialClientId, hasSavedCredentials }: CredentialsFormProps) {
  const { t } = useTranslation('settings');
  const idPrefix = useId();
  const [domain, setDomain] = useState(initialDomain);
  const [clientId, setClientId] = useState(initialClientId);
  const [clientSecret, setClientSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [result, setResult] = useState<SaveResult | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // A saved domain arriving from the server (or a refetch) re-seeds the box.
  useEffect(() => setDomain(initialDomain), [initialDomain]);
  useEffect(() => setClientId(initialClientId), [initialClientId]);

  const normalizedDomain = normalizeShopDomain(domain);
  const trimmedClientId = clientId.trim();
  const trimmedSecret = clientSecret.trim();
  const trimmedToken = accessToken.trim();
  const changedClientId = trimmedClientId !== initialClientId.trim();
  const hasPair = trimmedClientId !== '' && trimmedSecret !== '';
  // In the change-app form the Client ID is pre-filled, so only a typed secret makes a pair.
  const partialPair = variant === 'changeApp' ? changedClientId && trimmedSecret === '' : (trimmedClientId !== '') !== (trimmedSecret !== '');
  const domainInvalid = !SHOP_DOMAIN_PATTERN.test(normalizedDomain);
  const tokenAllowed = variant === 'connect' && connection.canUseOrgScope;
  // Without the org-scope save (platform page, another org) only a full pair can be saved.
  const needsPair = variant === 'changeApp' || !connection.canUseOrgScope;
  const usesToken = tokenAllowed && !hasPair && trimmedToken !== '';

  const hasChanges =
    normalizedDomain !== normalizeShopDomain(initialDomain) ||
    hasPair ||
    usesToken ||
    (variant === 'connect' ? trimmedClientId !== '' : changedClientId);
  const canSubmit = hasChanges && !domainInvalid && !partialPair && (!needsPair || hasPair) && !connection.isSaving;

  const reset = () => {
    setDomain(initialDomain);
    setClientId(initialClientId);
    setClientSecret('');
    setAccessToken('');
    setResult(null);
    setSubmitted(false);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;
    setResult(null);
    try {
      const outcome = await connection.save({
        shopDomain: normalizedDomain,
        ...(hasPair ? { clientId: trimmedClientId, clientSecret: trimmedSecret } : {}),
        ...(usesToken ? { accessToken: trimmedToken } : {}),
      });
      setResult(outcome);
      const success = successToast(t, outcome);
      if (success) {
        toast.success(success.title, success.description ? { description: success.description } : undefined);
        setClientSecret('');
        setAccessToken('');
        setSubmitted(false);
        if (variant === 'connect') setClientId('');
      }
    } catch (error) {
      toast.error(t('shopifyConnection.store.saveFailed'), {
        description: error instanceof Error ? error.message : undefined,
        duration: Infinity,
      });
    }
  };

  const ids = {
    domain: `${idPrefix}-domain`,
    clientId: `${idPrefix}-client-id`,
    clientSecret: `${idPrefix}-client-secret`,
    token: `${idPrefix}-access-token`,
    domainError: `${idPrefix}-domain-error`,
    pairError: `${idPrefix}-pair-error`,
  };
  const showDomainError = domainInvalid && (submitted || (domain.trim() !== '' && normalizedDomain !== normalizeShopDomain(initialDomain)));

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="space-y-4" noValidate autoComplete="off">
      <div className="max-w-prose space-y-4">
        <div className="space-y-2">
          <Label htmlFor={ids.domain}>
            {t('shopifyConnection.store.form.domain')}
            <span className="ml-1 text-destructive" aria-hidden="true">*</span>
          </Label>
          <NoAutofillTextInput
            id={ids.domain}
            name="shopify-store-domain"
            value={domain}
            onChange={(event) => {
              setDomain(event.target.value);
              setResult(null);
            }}
            placeholder="yourstore.myshopify.com"
            aria-required="true"
            aria-invalid={showDomainError ? true : undefined}
            aria-describedby={showDomainError ? ids.domainError : undefined}
          />
          {showDomainError ? (
            <p id={ids.domainError} className="text-sm text-destructive">
              {t('shopifyConnection.store.form.domainInvalid')}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">{t('shopifyConnection.store.form.domainHint')}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor={ids.clientId}>{t('shopifyConnection.store.form.clientId')}</Label>
          <NoAutofillTextInput
            id={ids.clientId}
            name="shopify-app-client-id"
            value={clientId}
            onChange={(event) => {
              setClientId(event.target.value);
              setResult(null);
            }}
            placeholder={t('shopifyConnection.store.form.clientIdPlaceholder')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={ids.clientSecret}>{t('shopifyConnection.store.form.clientSecret')}</Label>
          <LocalSecretInput
            id={ids.clientSecret}
            name="shopify-app-client-secret"
            revealable
            value={clientSecret}
            onChange={(event) => {
              setClientSecret(event.target.value);
              setResult(null);
            }}
            placeholder={
              hasSavedCredentials
                ? t('shopifyConnection.store.form.secretPlaceholderKeep')
                : t('shopifyConnection.store.form.secretPlaceholder')
            }
            aria-invalid={partialPair && submitted ? true : undefined}
            aria-describedby={partialPair ? ids.pairError : undefined}
          />
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            {variant === 'changeApp' ? t('shopifyConnection.store.form.changeAppHint') : t('shopifyConnection.store.form.secretHint')}
          </p>
          {partialPair && (
            <p id={ids.pairError} className="text-sm text-destructive">
              {t('shopifyConnection.store.form.partialPair')}
            </p>
          )}
          {needsPair && !hasPair && !partialPair && variant === 'connect' && (
            <p className="text-xs text-muted-foreground">{t('shopifyConnection.store.form.pairRequired')}</p>
          )}
        </div>

        {tokenAllowed && (
          <Collapsible open={showToken} onOpenChange={setShowToken}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-muted-foreground transition-colors duration-200 hover:text-foreground motion-reduce:transition-none"
              >
                {showToken ? <ChevronUp className="h-3 w-3" aria-hidden="true" /> : <ChevronDown className="h-3 w-3" aria-hidden="true" />}
                {t('shopifyConnection.store.form.tokenToggle')}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="space-y-2">
                <Label htmlFor={ids.token}>{t('shopifyConnection.store.form.token')}</Label>
                <LocalSecretInput
                  id={ids.token}
                  name="shopify-admin-api-token"
                  revealable
                  value={accessToken}
                  onChange={(event) => {
                    setAccessToken(event.target.value);
                    setResult(null);
                  }}
                  placeholder="shpat_..."
                />
                <p className="text-xs text-muted-foreground">{t('shopifyConnection.store.form.tokenHint')}</p>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>

      {variant === 'connect' && (
        <p className="flex items-start gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t('shopifyConnection.store.form.warning')}
        </p>
      )}

      {result && <ResultMessage result={result} />}

      {hasChanges && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/40 px-4 py-3">
          <p className="text-sm text-muted-foreground">{t('shopifyConnection.store.form.unsaved')}</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={reset} disabled={connection.isSaving}>
              {t('shopifyConnection.store.form.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={!canSubmit}>
              {connection.isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  {t('shopifyConnection.store.form.saving')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t('shopifyConnection.store.form.save')}
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}

function successToast(t: TFunction, result: SaveResult): { title: string; description?: string } | null {
  switch (result.kind) {
    case 'connected':
      if (result.mode === 'client_credentials') {
        return {
          title: t('shopifyConnection.store.result.connectedTitle'),
          description: t('shopifyConnection.store.result.connectedBody'),
        };
      }
      if (result.mode === 'access_token') return { title: t('shopifyConnection.store.result.tokenSaved') };
      return { title: t('shopifyConnection.store.result.appSaved') };
    case 'saved':
      return { title: t('shopifyConnection.store.result.domainSaved') };
    case 'install_required':
      return {
        title: t('shopifyConnection.store.result.installRequiredTitle'),
        description: t('shopifyConnection.store.result.installRequiredBody'),
      };
    default:
      return null;
  }
}

/** The outcomes that need reading (and acting on) stay inline until the next edit. */
function ResultMessage({ result }: { result: SaveResult }) {
  const { t } = useTranslation('settings');
  const message = useMemo(() => {
    switch (result.kind) {
      case 'needs_blue_monk':
        return { tone: 'amber' as const, text: t('shopifyConnection.store.result.needsBlueMonk') };
      case 'locked':
        return { tone: 'amber' as const, text: t('shopifyConnection.store.result.locked') };
      case 'error':
        return { tone: 'error' as const, text: saveErrorText(t, result.reason) };
      default:
        return null;
    }
  }, [result, t]);
  if (!message) return null;
  return message.tone === 'amber' ? (
    <AmberNote role="alert">{message.text}</AmberNote>
  ) : (
    <p className="text-sm text-destructive" role="alert">
      {message.text}
    </p>
  );
}
